<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { RouterLink, useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '@yuksalish/auth'
import { formatApiError } from '@yuksalish/api-client'
import { formatDisplayDate } from '@yuksalish/ui'
import { useFinanceApi } from '../composables/useFinanceApi'
import { formatCount, formatMoney } from '../utils/format'
import type { FinancialAccount, PayrollRunDetail } from '../types/finance'

/**
 * `/payroll/runs/:id` — employee lines with the earnings and deductions behind
 * them, plus the run lifecycle actions (recalculate → approve → post).
 */

interface BreakdownRow {
  id: string
  employee: string
  type: string
  description: string
  quantity: number | null
  unit: string | null
  amount: number
  currency: string
}

const route = useRoute()
const { t } = useI18n()
const auth = useAuthStore()
const {
  getPayrollRun,
  listFinancialAccounts,
  recalculatePayrollRun,
  approvePayrollRun,
  postPayrollRun,
} = useFinanceApi()

const runId = computed(() => String(route.params.id ?? ''))
const permissions = computed(() => auth.user?.permissions ?? [])
const canManage = computed(() => permissions.value.includes('payroll_runs.manage'))
const canApprove = computed(() => permissions.value.includes('payroll_runs.approve'))

const run = ref<PayrollRunDetail | null>(null)
const accounts = ref<FinancialAccount[]>([])
const loading = ref(true)
const error = ref('')
const actionError = ref('')
const actionMessage = ref('')
const busy = ref(false)
const accountId = ref<string | number>('')

const lines = computed(() => run.value?.lines ?? [])

const postableAccounts = computed(() =>
  accounts.value.filter((account) => account.is_active !== 0),
)

const currencyMatches = computed(() => {
  const account = postableAccounts.value.find((entry) => String(entry.id) === String(accountId.value))
  return Boolean(account) && account?.currency_code === run.value?.currency_code
})

const earnings = computed<BreakdownRow[]>(() =>
  lines.value.flatMap((line) =>
    (line.earnings ?? []).map((earning) => ({
      id: `e-${line.id}-${earning.id}`,
      employee: line.employee_name,
      type: earning.earning_type,
      description: earning.description || '—',
      quantity: earning.quantity ?? null,
      unit: earning.unit ?? null,
      amount: Number(earning.gross_amount ?? 0),
      currency: earning.currency_code,
    })),
  ),
)

const deductions = computed<BreakdownRow[]>(() =>
  lines.value.flatMap((line) =>
    (line.deductions ?? []).map((deduction) => ({
      id: `d-${line.id}-${deduction.id}`,
      employee: line.employee_name,
      type: deduction.deduction_type,
      description: deduction.description || '—',
      quantity: null,
      unit: null,
      amount: Number(deduction.amount ?? 0),
      currency: deduction.currency_code,
    })),
  ),
)

function statusLabel(status: string): string {
  const key = `finance.payrollFlow.runStatus${status.charAt(0).toUpperCase()}${status.slice(1)}`
  const label = t(key)
  return label.startsWith('finance.') ? status : label
}

async function load() {
  loading.value = true
  error.value = ''
  try {
    run.value = await getPayrollRun(runId.value)
    if (!accountId.value && run.value.currency_code) {
      const match = postableAccounts.value.find(
        (account) => account.currency_code === run.value?.currency_code,
      )
      accountId.value = match?.id ?? ''
    }
  } catch (cause) {
    error.value = formatApiError(cause)
    run.value = null
  } finally {
    loading.value = false
  }
}

async function loadAccounts() {
  try {
    // Only active accounts are postable, so the filter matches the API rule.
    accounts.value = await listFinancialAccounts({ is_active: 1 })
  } catch {
    accounts.value = []
  }
}

async function recalculate() {
  busy.value = true
  actionError.value = ''
  actionMessage.value = ''
  try {
    await recalculatePayrollRun(runId.value)
    actionMessage.value = t('finance.payrollFlow.runs.recalculated')
    await load()
  } catch (cause) {
    actionError.value = formatApiError(cause)
  } finally {
    busy.value = false
  }
}

async function approve() {
  busy.value = true
  actionError.value = ''
  actionMessage.value = ''
  try {
    await approvePayrollRun(runId.value)
    actionMessage.value = t('finance.payrollFlow.runs.approvedRun')
    await load()
  } catch (cause) {
    actionError.value = formatApiError(cause)
  } finally {
    busy.value = false
  }
}

async function post() {
  if (!accountId.value) {
    actionError.value = t('finance.payrollFlow.runs.accountRequired')
    return
  }
  if (!window.confirm(t('finance.payrollFlow.runs.postConfirm', { code: run.value?.code ?? '' }))) {
    return
  }
  busy.value = true
  actionError.value = ''
  actionMessage.value = ''
  try {
    await postPayrollRun(runId.value, accountId.value)
    actionMessage.value = t('finance.payrollFlow.runs.posted')
    await load()
  } catch (cause) {
    actionError.value = formatApiError(cause)
  } finally {
    busy.value = false
  }
}

onMounted(() => {
  void loadAccounts()
  void load()
})
</script>

<template>
  <div class="page-heading">
    <div>
      <p class="eyebrow">{{ t('finance.payrollFlow.runs.eyebrow') }}</p>
      <h2>{{ run?.code || t('finance.payrollFlow.runs.detailTitle') }}</h2>
      <p v-if="run">
        {{ run.payroll_period_code || '—' }} · {{ run.currency_code }} ·
        {{ run.date_from ? formatDisplayDate(run.date_from) : '—' }} –
        {{ run.date_to ? formatDisplayDate(run.date_to) : '—' }}
      </p>
    </div>
    <RouterLink class="btn-secondary" to="/payroll/runs">
      {{ t('finance.common.back') }}
    </RouterLink>
  </div>

  <p v-if="actionError" class="error-inline">{{ actionError }}</p>
  <p v-if="actionMessage" class="action-message">{{ actionMessage }}</p>

  <div v-if="loading" class="state-message"><p>{{ t('finance.common.loading') }}</p></div>

  <div v-else-if="error" class="state-message error">
    <p>{{ error }}</p>
    <button class="btn-primary" @click="load">{{ t('finance.common.retry') }}</button>
  </div>

  <template v-else-if="run">
    <div class="summary-row">
      <article class="summary-card accent">
        <span class="summary-value">{{ formatMoney(run.gross_total, run.currency_code) }}</span>
        <span class="summary-label">{{ t('finance.payrollFlow.runs.gross') }}</span>
      </article>
      <article class="summary-card">
        <span class="summary-value">{{ formatMoney(run.deduction_total, run.currency_code) }}</span>
        <span class="summary-label">{{ t('finance.payrollFlow.runs.deductions') }}</span>
      </article>
      <article class="summary-card">
        <span class="summary-value">{{ formatMoney(run.net_total, run.currency_code) }}</span>
        <span class="summary-label">{{ t('finance.payrollFlow.runs.net') }}</span>
      </article>
      <article class="summary-card">
        <span class="summary-value">{{ formatCount(lines.length) }}</span>
        <span class="summary-label">{{ t('finance.payrollFlow.runs.lines') }}</span>
      </article>
    </div>

    <div class="toolbar">
      <span class="status-badge" :class="`status-${run.status}`">{{ statusLabel(run.status) }}</span>
      <span class="spacer" />
      <button
        v-if="canManage && run.status === 'draft'"
        class="btn-secondary"
        type="button"
        :disabled="busy"
        @click="recalculate"
      >
        {{ t('finance.payrollFlow.runs.recalculate') }}
      </button>
      <button
        v-if="canApprove && run.status === 'draft'"
        class="btn-primary"
        type="button"
        :disabled="busy || !lines.length"
        @click="approve"
      >
        {{ t('finance.payrollFlow.runs.approve') }}
      </button>
    </div>

    <section v-if="canApprove && run.status === 'approved'" class="form-card">
      <h3>
        {{ t('finance.payrollFlow.runs.post') }}
        <small>{{ t('finance.payrollFlow.runs.accountHint') }}</small>
      </h3>
      <div class="form-grid tight">
        <div class="field">
          <label for="run-account">{{ t('finance.payrollFlow.runs.account') }}</label>
          <select id="run-account" v-model="accountId">
            <option value="">{{ t('finance.common.allAccounts') }}</option>
            <option v-for="account in postableAccounts" :key="account.id" :value="account.id">
              {{ account.account_code || account.name }} · {{ account.currency_code }}
            </option>
          </select>
          <small v-if="accountId && !currencyMatches" class="error-inline">
            {{ t('finance.payrollFlow.runs.accountCurrencyMismatch', { currency: run.currency_code }) }}
          </small>
        </div>
      </div>
      <div class="modal-actions">
        <button
          class="btn-primary"
          type="button"
          :disabled="busy || !accountId || !currencyMatches"
          @click="post"
        >
          {{ t('finance.payrollFlow.runs.post') }}
        </button>
      </div>
    </section>

    <h3>{{ t('finance.payrollFlow.runs.lineTitle') }}</h3>

    <p v-if="!lines.length" class="state-message">{{ t('finance.payrollFlow.runs.noLines') }}</p>

    <div v-else class="table-scroll">
      <table class="data-table">
        <thead>
          <tr>
            <th>{{ t('finance.payrollFlow.runs.employee') }}</th>
            <th>{{ t('finance.payrollFlow.runs.department') }}</th>
            <th class="cell-num">{{ t('finance.payrollFlow.runs.gross') }}</th>
            <th class="cell-num">{{ t('finance.payrollFlow.runs.deductions') }}</th>
            <th class="cell-num">{{ t('finance.payrollFlow.runs.net') }}</th>
            <th>{{ t('finance.common.status') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="line in lines" :key="line.id">
            <td class="cell-name">
              {{ line.employee_name }}
              <span class="cell-sub">{{ line.employee_code || '—' }}</span>
            </td>
            <td>{{ line.department_name || '—' }}</td>
            <td class="cell-num">{{ formatMoney(line.gross_amount, line.currency_code) }}</td>
            <td class="cell-num">{{ formatMoney(line.deduction_amount, line.currency_code) }}</td>
            <td class="cell-num">{{ formatMoney(line.net_amount, line.currency_code) }}</td>
            <td>{{ line.status }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <template v-if="earnings.length">
      <h3>{{ t('finance.payrollFlow.runs.earnings') }}</h3>
      <div class="table-scroll">
        <table class="data-table compact">
          <thead>
            <tr>
              <th>{{ t('finance.payrollFlow.runs.employee') }}</th>
              <th>{{ t('finance.common.type') }}</th>
              <th>{{ t('finance.common.detail') }}</th>
              <th class="cell-num">{{ t('finance.payrollFlow.runs.quantity') }}</th>
              <th class="cell-num">{{ t('finance.payrollFlow.runs.amount') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in earnings" :key="row.id">
              <td class="cell-name">{{ row.employee }}</td>
              <td>{{ row.type }}</td>
              <td>{{ row.description }}</td>
              <td class="cell-num">
                {{ row.quantity === null ? '—' : `${formatCount(row.quantity)} ${row.unit || ''}` }}
              </td>
              <td class="cell-num">{{ formatMoney(row.amount, row.currency) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </template>

    <template v-if="deductions.length">
      <h3>{{ t('finance.payrollFlow.runs.deductionLines') }}</h3>
      <div class="table-scroll">
        <table class="data-table compact">
          <thead>
            <tr>
              <th>{{ t('finance.payrollFlow.runs.employee') }}</th>
              <th>{{ t('finance.common.type') }}</th>
              <th>{{ t('finance.common.detail') }}</th>
              <th class="cell-num">{{ t('finance.payrollFlow.runs.amount') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in deductions" :key="row.id">
              <td class="cell-name">{{ row.employee }}</td>
              <td>{{ row.type }}</td>
              <td>{{ row.description }}</td>
              <td class="cell-num">{{ formatMoney(row.amount, row.currency) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </template>
  </template>
</template>

<style scoped>
.status-draft {
  border-color: #cfd8d1;
  color: #5f6b62;
  background: #f6f8f6;
}

.status-approved,
.status-paid {
  border-color: #4e6651;
  color: #2f4a33;
  background: #eef4f0;
}

.status-posted {
  border-color: #7fa6d9;
  color: #274b7a;
  background: #f0f5fb;
}

.status-reversed {
  border-color: #e0b0a6;
  color: #a13c2c;
  background: #fdf3f1;
}
</style>
